import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Loader2, Zap, BarChart3, Archive, Flag, Clock, Layers } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { ConcentricRings } from '@/components/Motifs';
import { usePageTitle } from '@/hooks/usePageTitle';
import api from '@/lib/api';
import { toast } from 'sonner';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] }
  })
};

const Section = ({ children, className = '' }) => (
  <motion.section
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: '-80px' }}
    variants={fadeUp}
    className={`py-16 sm:py-24 ${className}`}
  >
    {children}
  </motion.section>
);

const MATRIX_CELLS = [
  // Row 1 - High Impact
  { label: 'Quick Wins', action: 'DO FIRST', row: 'High Impact', col: 'Low Effort', accent: 'sage' },
  { label: 'High-Value', action: 'PLAN IT', row: 'High Impact', col: 'Med Effort', accent: 'gold' },
  { label: 'Big Projects', action: 'SCHEDULE', row: 'High Impact', col: 'High Effort', accent: 'gold' },
  // Row 2 - Medium Impact
  { label: 'Easy Gains', action: 'BATCH', row: 'Med Impact', col: 'Low Effort', accent: 'sage' },
  { label: 'Steady Work', action: 'PRIORITIZE', row: 'Med Impact', col: 'Med Effort', accent: 'silver' },
  { label: 'Consider', action: 'RE-ASSESS', row: 'Med Impact', col: 'High Effort', accent: 'silver' },
  // Row 3 - Low Impact
  { label: 'Fill-Ins', action: 'DELEGATE', row: 'Low Impact', col: 'Low Effort', accent: 'silver' },
  { label: 'Low Priority', action: 'IF TIME', row: 'Low Impact', col: 'Med Effort', accent: 'rose' },
  { label: 'Avoid', action: 'DROP/DEFER', row: 'Low Impact', col: 'High Effort', accent: 'rose' },
];

export default function PlannerPage() {
  usePageTitle('Prism Planner');
  const [purchasing, setPurchasing] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const handlePurchase = async () => {
    if (!email) {
      toast.error('Please enter your email address.');
      return;
    }
    setPurchasing(true);
    try {
      const originUrl = window.location.origin;
      const res = await api.post('/checkout', {
        product_slug: 'prism-planner',
        origin_url: originUrl,
        quantity: 1,
        customer_email: email,
        customer_name: name
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment processing error. Please try again.');
      setPurchasing(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SiteHeader />

      {/* ── HERO ──────────────────────────────────────────── */}
      <section className="hero-gradient relative py-20 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1608549785765-036974ba067d?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85')` }} />
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.12 } } }}
            >
              <motion.p variants={fadeUp} className="font-['Inter'] text-[11px] font-medium uppercase tracking-[0.2em] text-[hsl(var(--gold))] mb-6">
                Instrument
              </motion.p>
              <motion.h1 variants={fadeUp} className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-[60px] font-bold leading-[1.08] text-[hsl(var(--foreground))] mb-4">
                Prism Planner
              </motion.h1>
              <motion.p variants={fadeUp} className="font-['Cormorant_Garamond'] text-xl sm:text-2xl italic text-[hsl(var(--champagne))] mb-6">
                Drag. Prioritize. Execute.
              </motion.p>
              <motion.p variants={fadeUp} className="font-['Inter'] text-base sm:text-lg leading-[1.8] text-[hsl(var(--muted-foreground))] mb-8 max-w-lg">
                A personal planning tool that adapts to your energy. Drop tasks into a 9-quadrant priority matrix, and let the system build your to-do list &mdash; automatically reordered based on how you're feeling today.
              </motion.p>
              <motion.div variants={fadeUp} className="flex items-center gap-4">
                <span className="font-['Cormorant_Garamond'] text-3xl font-bold text-[hsl(var(--gold))]">$49</span>
                <span className="font-['Inter'] text-xs text-[hsl(var(--silver))]">Lifetime access &middot; All future updates</span>
              </motion.div>
            </motion.div>

            {/* Right side - 9-quadrant matrix visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="hidden lg:block"
            >
              <div className="rounded-xl border border-[rgba(201,168,76,0.2)] bg-[hsl(var(--card))] p-5 shadow-[0_18px_60px_rgba(0,0,0,0.45)]">
                {/* Column headers */}
                <div className="grid grid-cols-3 gap-2 mb-2 pl-[2px]">
                  {['Low Effort', 'Med Effort', 'High Effort'].map(h => (
                    <p key={h} className="font-['Inter'] text-[8px] font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] text-center">{h}</p>
                  ))}
                </div>
                {/* Matrix grid */}
                <div className="grid grid-cols-3 gap-2">
                  {MATRIX_CELLS.map((cell) => (
                    <div
                      key={cell.label}
                      className={`rounded-lg border p-3 min-h-[72px] transition-colors duration-300 ${
                        cell.accent === 'sage' ? 'border-[rgba(122,158,126,0.2)] bg-[rgba(122,158,126,0.06)]' :
                        cell.accent === 'gold' ? 'border-[rgba(201,168,76,0.15)] bg-[rgba(201,168,76,0.04)]' :
                        cell.accent === 'rose' ? 'border-[rgba(185,105,122,0.12)] bg-[rgba(185,105,122,0.03)]' :
                        'border-[rgba(168,181,178,0.1)] bg-[rgba(168,181,178,0.03)]'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-1">
                        <p className="font-['Cormorant_Garamond'] text-[11px] font-semibold italic text-[hsl(var(--foreground))]">{cell.label}</p>
                        <p className="font-['Inter'] text-[7px] font-medium uppercase tracking-[0.1em] text-[hsl(var(--silver))]">{cell.action}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Row labels */}
                <div className="flex justify-between mt-2 px-1">
                  <span className="font-['Inter'] text-[7px] uppercase tracking-[0.1em] text-[hsl(var(--sage))]">High Impact</span>
                  <span className="font-['Inter'] text-[7px] uppercase tracking-[0.1em] text-[hsl(var(--silver))]">Med</span>
                  <span className="font-['Inter'] text-[7px] uppercase tracking-[0.1em] text-[hsl(var(--rose))]">Low Impact</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ──────────────────────────────────── */}
      <Section>
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-6">How it works</p>
          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[hsl(var(--foreground))] mb-12 leading-[1.15]">
            Your mind thinks in patterns.<br />
            <em className="font-normal italic text-[hsl(var(--champagne))]">Your planner should too.</em>
          </h2>

          <div className="space-y-8">
            {[
              { num: '01', icon: Layers, title: 'Capture tasks instantly', desc: 'Type a task and categorize it as Daily, Weekly, or Monthly. Save repeating tasks as templates for one-tap reuse. Everything starts in your Inbox.' },
              { num: '02', icon: null, title: 'Drag into a 3x3 priority matrix', desc: 'Nine quadrants mapped across Impact (high/med/low) and Effort (low/med/high). Drag tasks where they belong \u2014 from Quick Wins to Big Projects to things you should Avoid entirely.' },
              { num: '03', icon: Zap, title: 'Set your energy level', desc: 'Choose High, Medium, or Low energy. Your "Next Up" to-do list automatically re-sorts itself \u2014 quick wins rise when you\u2019re tired, big projects surface when you\u2019re sharp.' },
              { num: '04', icon: Flag, title: 'Flag what\u2019s urgent', desc: 'Mark any task as urgent to boost it to the top of your list, no matter where it sits in the matrix. Urgency overrides the energy algorithm when it needs to.' },
              { num: '05', icon: Clock, title: 'Track everything with the task log', desc: 'A built-in activity log records every action \u2014 creation, completion, archival \u2014 with timestamps and metadata. Nothing is ever unaccounted for.' },
              { num: '06', icon: BarChart3, title: 'Review your week', desc: 'A productivity report shows completion rates, average time-to-done, category breakdowns, and trend comparisons week over week. See your patterns. Refine your system.' },
              { num: '07', icon: Archive, title: 'Archive and restore', desc: 'Start a new day or archive a session. Your history is preserved. Restore any previous session to review or continue where you left off. Nothing is ever lost.' },
            ].map((step) => (
              <motion.div key={step.num} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={parseInt(step.num)} variants={fadeUp}
                className="flex items-start gap-5"
              >
                <div className="shrink-0 w-10 h-10 rounded-lg bg-[rgba(201,168,76,0.08)] border border-[rgba(201,168,76,0.15)] flex items-center justify-center">
                  <span className="font-['Inter'] text-[11px] font-medium text-[hsl(var(--gold))]">{step.num}</span>
                </div>
                <div>
                  <p className="font-['Cormorant_Garamond'] text-lg sm:text-xl font-semibold text-[hsl(var(--foreground))] mb-1.5">{step.title}</p>
                  <p className="font-['Inter'] text-sm leading-[1.7] text-[hsl(var(--muted-foreground))]">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── THE MATRIX EXPLAINED ──────────────────────────── */}
      <Section className="section-mist">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-6">The 9-Quadrant System</p>
          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[hsl(var(--foreground))] mb-4 leading-[1.15]">
            Not a to-do list. A <em className="font-normal italic text-[hsl(var(--champagne))]">thinking instrument.</em>
          </h2>
          <p className="font-['Inter'] text-[15px] leading-[1.8] text-[hsl(var(--muted-foreground))] mb-10">
            Most planners organize tasks by time. Prism organizes them by <strong className="text-[hsl(var(--foreground))]">impact and effort</strong> &mdash; then adapts the execution order to your energy. The result: you always know what to do next, and it's always calibrated to your actual capacity.
          </p>

          {/* Matrix grid - mobile friendly */}
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            {MATRIX_CELLS.map((cell) => (
              <div
                key={cell.label}
                className={`rounded-xl border p-3 sm:p-4 transition-colors duration-300 ${
                  cell.accent === 'sage' ? 'border-[rgba(122,158,126,0.25)] bg-[rgba(122,158,126,0.08)]' :
                  cell.accent === 'gold' ? 'border-[rgba(201,168,76,0.2)] bg-[rgba(201,168,76,0.06)]' :
                  cell.accent === 'rose' ? 'border-[rgba(185,105,122,0.15)] bg-[rgba(185,105,122,0.04)]' :
                  'border-[rgba(168,181,178,0.12)] bg-[rgba(168,181,178,0.04)]'
                }`}
              >
                <p className="font-['Cormorant_Garamond'] text-sm sm:text-base font-semibold italic text-[hsl(var(--foreground))] mb-0.5">{cell.label}</p>
                <p className="font-['Inter'] text-[8px] sm:text-[9px] font-medium uppercase tracking-[0.1em] text-[hsl(var(--silver))]">{cell.action}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── BUILT FOR ─────────────────────────────────────── */}
      <Section>
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-6">Built for</p>
          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[hsl(var(--foreground))] mb-8 leading-[1.15]">
            The thinker who has more ideas<br />than hours in the day.
          </h2>
          <div className="space-y-4">
            {[
              'You think in patterns and connections, which makes linear task lists feel insufficient.',
              'Your energy fluctuates, and you need a system that adapts to your actual capacity.',
              'You want to see the landscape of your commitments, not just a scrolling list.',
              'You value structured execution but need flexibility in how you approach it.',
              'You\u2019re building something that requires sustained focus across multiple timescales.',
            ].map((item, i) => (
              <motion.div key={item.slice(0, 25)} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp}
                className="flex items-start gap-3"
              >
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[hsl(var(--gold))] shrink-0" />
                <span className="font-['Inter'] text-[15px] leading-[1.7] text-[hsl(var(--muted-foreground))]">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── WHAT'S INCLUDED ───────────────────────────────── */}
      <Section className="section-mist">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-6">What you get</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              { title: '9-quadrant priority matrix', desc: 'Drag-and-drop tasks across Impact x Effort dimensions.' },
              { title: 'Energy-adaptive sorting', desc: 'To-do list reorders based on High, Medium, or Low energy.' },
              { title: 'Task templates', desc: 'Save and reuse recurring tasks with one tap.' },
              { title: 'Urgent flagging', desc: 'Override the algorithm when something can\u2019t wait.' },
              { title: 'Complete activity log', desc: 'Timestamped record of every task action.' },
              { title: 'Weekly productivity report', desc: 'Completion rates, time-to-done, trends.' },
              { title: 'Session archive & restore', desc: 'Start fresh daily. Restore any previous session.' },
              { title: 'Lifetime access', desc: 'One payment. No subscriptions. All future updates.' },
            ].map((item, i) => (
              <motion.div key={item.title} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp}
                className="flex items-start gap-3"
              >
                <CheckCircle className="h-4 w-4 text-[hsl(var(--sage))] mt-0.5 shrink-0" />
                <div>
                  <p className="font-['Inter'] text-sm font-medium text-[hsl(var(--foreground))]">{item.title}</p>
                  <p className="font-['Inter'] text-xs text-[hsl(var(--muted-foreground))]">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── PURCHASE ──────────────────────────────────────── */}
      <Section>
        <div className="max-w-xl mx-auto px-4 sm:px-6 text-center">
          <div className="rounded-xl bg-[hsl(var(--card))] border border-[rgba(201,168,76,0.25)] p-8 sm:p-12 shadow-[0_18px_60px_rgba(0,0,0,0.45)] relative overflow-hidden">
            <div className="absolute top-0 left-0 w-12 h-12 border-t border-l border-[rgba(201,168,76,0.3)]" />
            <div className="absolute bottom-0 right-0 w-12 h-12 border-b border-r border-[rgba(201,168,76,0.3)]" />

            <p className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-bold text-[hsl(var(--foreground))] mb-3">
              Prism Planner
            </p>
            <p className="font-['Inter'] text-sm text-[hsl(var(--silver))] mb-4">
              Lifetime Access &middot; All Future Updates
            </p>
            <p className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[hsl(var(--gold))] mb-2">
              $49
            </p>
            <p className="font-['Inter'] text-xs text-[hsl(var(--muted-foreground))] mb-8">
              One-time payment &middot; No subscription
            </p>

            <div className="space-y-3 max-w-sm mx-auto mb-6">
              <Input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Your name"
                className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] font-['Inter'] text-sm"
              />
              <Input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Your email address"
                className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] font-['Inter'] text-sm"
                data-testid="planner-checkout-email"
                required
              />
              <Button
                onClick={handlePurchase}
                disabled={purchasing}
                className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium py-6 rounded-lg hover:brightness-[1.03] disabled:opacity-50"
                data-testid="planner-checkout-button"
              >
                {purchasing ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
                ) : (
                  <>Get Lifetime Access <ArrowRight className="h-4 w-4 ml-2" /></>
                )}
              </Button>
            </div>
            <p className="font-['Inter'] text-[11px] text-[hsl(var(--muted-foreground))]">
              Secure payment via Stripe. Access delivered immediately after purchase.
            </p>
          </div>
        </div>
      </Section>

      <SiteFooter />
    </div>
  );
}
